import amplitude from './analytics';
import { getEventMetaData } from './utils';

/*
user_id is no longer a required attribute. 
user_id is used to associate that device/session with our unique UUID
user_id should not be set other than at user creation and login
user_id should not be set if we don't know what the user_id is 
(null users get grouped as one user and it leads to merge issues)
*/
export function ampLogEvent(event: string, payload?: any, cookies?: {[name: string]: any} | undefined) {
    if (typeof window === 'undefined') {
        return;
    }
    const { event_source, event_device } = getEventMetaData(cookies);
    amplitude.track(event, {
        ...payload,
        event_source,
        event_device,
        analytics_version: 'v2'
    });
}

/**
 * Sends queued events now, before the page leaves for another site (Stripe), which would drop them.
 * Resolves within `timeoutMs` either way, so a slow send never holds the member up.
 */
export async function ampFlush(timeoutMs = 1000): Promise<void> {
    if (typeof window === 'undefined') {
        return;
    }
    await Promise.race([
        amplitude.flush().promise.catch(() => undefined),
        new Promise(resolve => setTimeout(resolve, timeoutMs))
    ]);
}

/**
 * Current Amplitude session id. The SDK keeps it in a cookie, so it survives page refreshes and
 * only changes after the session timeout. Undefined on the server or where Amplitude isn't loaded.
 */
export function ampGetSessionId(): number | undefined {
    if (typeof window === 'undefined') {
        return undefined;
    }
    return amplitude.getSessionId();
}

export function ampIdentifySet(key: string, value: string) {
    if (typeof window === 'undefined') {
        return;
    }
    const identify = new amplitude.Identify().set(key, value);
    amplitude.identify(identify);
}

export function ampIdentifySetMultiple(tuples: {key: string, value: string}[]) {
    if (typeof window === 'undefined') {
        return;
    }
    const identify = new amplitude.Identify();
    tuples.forEach(tuple => {
        identify.set(tuple.key, tuple.value);
    });
    amplitude.identify(identify);
}

export function ampSetOnce(key: string, value: any) {
    if (typeof window === 'undefined') {
        return;
    }
    const firstPageVisit = new amplitude.Identify().setOnce(key, value);
    amplitude.identify(firstPageVisit);
}

export function ampSetUserId(userId: string) {
    if (typeof window === 'undefined') {
        return;
    }
    amplitude.setUserId(userId);
}
